import { useCallback, useState } from "react";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import BackButton from "../../contract/backButton";
import User from "../user";
import CustomizedSnackbars from "../../customizedSnackbars";
import StatusPill from "../../../pages/cashouts/CashoutCard/StatusPill";
import { AlertColor } from "@mui/material";
import PrimaryButton, {
  PrimaryButtonSize,
  PrimaryButtonType,
} from "../PrimaryButton";
import Spinner from "../Spinner";
import Modal from "../Modal";
import Paginations from "../Pagination";
import TransactionTable from "./TransactionTable";
import styles from "./DetailsPageLayout.module.scss";

export interface TableItem {
  id: string;
  title: string;
  contractId?: string;
  amount: number;
  [key: string]: unknown;
}

export interface UserData {
  profile?: {
    fullName?: string;
    photoUrl?: string;
    addresses?: unknown[];
  };
  phoneNumber?: string;
  nationalIdVerified?: boolean;
}
export interface ContractsTableProps {
  transactionsSectionTitle: string;
  showAllLinkText?: string;
  items: TableItem[];
  previewItems: TableItem[];
  tableHeaders: {
    [key: string]: string;
  };
  // Custom renderers
  renderTableRow?: (item: TableItem, index: number) => React.ReactNode;
  getItemStatus?: (item: TableItem, overallStatus?: string) => string;
  onContractClick?: (contractId: string) => void;

  // Pagination
  totalPages: number;
  onPageChange: (page: number) => void;
  currentPage: number;
}

export interface DetailsPageConfig {
  // Titles and labels
  transactionsCountLabel?: string;
  numberOfTransactions?: number;
  totalAmountLabel?: string;
  referenceNumberLabel?: string;
  actionButtonText?: string;
  addressErrorText?: string;

  transactionContracts: ContractsTableProps;
  refundContracts?: ContractsTableProps;

  // Data
  userData?: UserData;
  totalAmount?: number;
  referenceNumber?: string;
  status?: string;

  // Callbacks
  onBackClick?: () => void;
  onActionButtonClick?: () => void;

  // Configuration
  showActionButton?: boolean;
  actionButtonDisabled?: boolean;
  showReferenceNumber?: boolean;
  showAddressError?: boolean;
  currencyLabel?: string;

  // Loading states
  pageLoading?: boolean;
  actionLoading?: boolean;
}

enum SelectedModalTable {
  SELLER_AND_BROKER = "sellerAndBroker",
  BUYER = "buyer",
}

const DetailsPageLayout = ({
  transactionContracts,
  numberOfTransactions = 0,
  refundContracts,
  transactionsCountLabel,
  totalAmountLabel,
  referenceNumberLabel,
  actionButtonText,
  addressErrorText,
  userData,
  totalAmount,
  referenceNumber,
  status,

  onBackClick,
  onActionButtonClick,

  showActionButton = false,
  actionButtonDisabled = false,
  showReferenceNumber = false,
  showAddressError = false,
  currencyLabel,
  pageLoading = true,
  actionLoading = false,
}: DetailsPageConfig) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [snackBarOpen, setSnackBarOpen] = useState<boolean>(false);
  const [snackBarColor, setSnackBarColor] = useState<AlertColor>("info");
  const [snackBarTitle, setSnackBarTitle] = useState<string>("");
  const [selectedModalTable, setSelectedModalTable] =
    useState<SelectedModalTable | null>(null);

  const getSelectedTable = useCallback(
    (selectedModalType: SelectedModalTable | null) => {
      if (!selectedModalType) return null;
      return selectedModalType === SelectedModalTable.SELLER_AND_BROKER
        ? transactionContracts
        : refundContracts;
    },
    [transactionContracts, refundContracts]
  );

  const handleShowAllContractsModal = useCallback(
    (newSelectedModalTable: SelectedModalTable) => {
      setSelectedModalTable(newSelectedModalTable);
      //TODO: The problem here is that it opens the modal with old request data before the update
      const selectedTable = getSelectedTable(newSelectedModalTable);
      if (selectedTable) {
        selectedTable.onPageChange(selectedTable.currentPage ?? 0);
      }
      setModalOpen(true);
    },
    [getSelectedTable]
  );

  const handleCopy = useCallback(
    async (text: string) => {
      try {
        await navigator?.clipboard?.writeText(text);
        setSnackBarOpen(true);
        setSnackBarColor("success");
        setSnackBarTitle(t("COPIED"));
      } catch (error) {
        setSnackBarOpen(true);
        setSnackBarColor("error");
        setSnackBarTitle(JSON.stringify(error));
        console.error(error);
      }
    },
    [t]
  );

  const renderSnackBar = () => {
    return snackBarOpen ? (
      <CustomizedSnackbars
        open={snackBarOpen}
        title={snackBarTitle}
        type={snackBarColor}
      />
    ) : null;
  };

  const renderModalList = useCallback(() => {
    const selectedTable = getSelectedTable(selectedModalTable);
    if (!selectedTable) return null;

    return (
      <Modal
        customModalClassName={classNames(
          styles.modalContent,
          selectedTable.items?.length && selectedTable.items?.length > 10
            ? styles.modalMinHeightWhenFilled
            : ""
        )}
        visible={modalOpen}
        onCancel={() => {
          setModalOpen(false);
        }}
        size="large">
        <div>
          {actionLoading ? (
            <div className={styles.spinner_container}>
              <Spinner />
            </div>
          ) : (
            <div className={classNames(styles.request_section, styles.mb_16)}>
              <TransactionTable
                transactionsSectionTitle={
                  selectedTable.transactionsSectionTitle ?? ""
                }
                pageLoading={actionLoading}
                items={selectedTable.items ?? []}
                tableHeaders={selectedTable.tableHeaders ?? {}}
                renderTableRow={selectedTable.renderTableRow}
                getItemStatus={selectedTable.getItemStatus}
                status={status}
                onContractClick={selectedTable.onContractClick}
                onCopy={handleCopy}
                currencyLabel={currencyLabel}
              />
            </div>
          )}
          <div
            className={
              selectedTable?.items?.length && selectedTable?.items?.length > 10
                ? "paginatorContainer"
                : ""
            }>
            <Paginations
              pagesCount={selectedTable.totalPages}
              length={selectedTable.totalPages}
              page={selectedTable.currentPage}
              handlePaginationChange={selectedTable.onPageChange}
            />
          </div>
        </div>
      </Modal>
    );
  }, [
    getSelectedTable,
    selectedModalTable,
    modalOpen,
    actionLoading,
    status,
    handleCopy,
    currencyLabel,
  ]);

  return (
    <div className={styles.outer_container}>
      {pageLoading ? (
        <div className={styles.loadingContainer}>
          <Spinner />
        </div>
      ) : (
        <div className={styles.container}>
          <div className={classNames(styles.top_section, styles.card)}>
            {onBackClick && <BackButton onClick={onBackClick} />}
            <div className={styles.user_info_wrapper}>
              {userData && (
                <User
                  name={userData.profile?.fullName}
                  phoneNumber={userData.phoneNumber}
                  imageUrl={userData.profile?.photoUrl}
                  nationalIdVerified={userData.nationalIdVerified}
                />
              )}
              {transactionsCountLabel && numberOfTransactions !== undefined && (
                <p className={styles.transactions_count}>
                  {transactionsCountLabel}: {numberOfTransactions} معاملات
                </p>
              )}
            </div>
          </div>
          <TransactionTable
            transactionsSectionTitle={
              transactionContracts.transactionsSectionTitle
            }
            showAllLinkText={transactionContracts.showAllLinkText}
            onShowAllClick={() =>
              handleShowAllContractsModal(SelectedModalTable.SELLER_AND_BROKER)
            }
            pageLoading={pageLoading}
            items={transactionContracts.previewItems ?? []}
            tableHeaders={transactionContracts.tableHeaders}
            renderTableRow={transactionContracts.renderTableRow}
            getItemStatus={transactionContracts.getItemStatus}
            status={status}
            onContractClick={transactionContracts.onContractClick}
            onCopy={handleCopy}
            currencyLabel={currencyLabel}
          />
          <div className={classNames(styles.summary_section, styles.card)}>
            <div className={styles.summary_content}>
              <div className={styles.summary_info_horizontal}>
                {totalAmount !== undefined && (
                  <div className={styles.summary_info}>
                    <div className={styles.total_label}>
                      {totalAmountLabel || t("lbl_total_amount_of_transfer")}
                    </div>
                    <div className={styles.total_amount}>
                      {totalAmount.toLocaleString()}{" "}
                      {currencyLabel || t("lbl_saudi_currency")}
                    </div>
                  </div>
                )}
                {showReferenceNumber && referenceNumber && (
                  <div className={styles.summary_info}>
                    <p className={styles.total_label}>
                      {referenceNumberLabel || t("lbl_reference_number")}
                    </p>
                    <div className={styles.reference_number_container}>
                      <p className={styles.reference_number}>
                        {referenceNumber}
                      </p>
                      <img
                        src="/assets/images/svg/copy.svg"
                        alt="copy"
                        onClick={() => handleCopy(referenceNumber)}
                        className={styles.copy_icon}
                      />
                    </div>
                  </div>
                )}
              </div>
              <div className={styles.transfer_section}>
                {status && (
                  <div className={styles.transfer_status}>
                    <StatusPill status={status} />
                  </div>
                )}
                {showActionButton && onActionButtonClick && (
                  <PrimaryButton
                    disabled={actionButtonDisabled || actionLoading}
                    buttonType={
                      actionButtonDisabled
                        ? PrimaryButtonType.DISABLED
                        : PrimaryButtonType.DEFAULT
                    }
                    size={PrimaryButtonSize.LARGE}
                    onClick={onActionButtonClick}
                    className={classNames(styles.transfer_button, {
                      [styles.transfer_button_disabled]:
                        actionButtonDisabled || actionLoading,
                    })}>
                    {actionLoading ? (
                      <Spinner />
                    ) : (
                      actionButtonText || t("lbl_release")
                    )}
                  </PrimaryButton>
                )}
                {showAddressError && addressErrorText && (
                  <p className={styles.address_error}>{addressErrorText}</p>
                )}
              </div>
            </div>
          </div>
          {refundContracts && (
            <TransactionTable
              transactionsSectionTitle={
                refundContracts.transactionsSectionTitle
              }
              showAllLinkText={refundContracts.showAllLinkText}
              onShowAllClick={() =>
                handleShowAllContractsModal(SelectedModalTable.BUYER)
              }
              pageLoading={pageLoading}
              items={refundContracts?.previewItems ?? []}
              tableHeaders={refundContracts?.tableHeaders}
              renderTableRow={refundContracts.renderTableRow}
              getItemStatus={refundContracts?.getItemStatus}
              status={status}
              onContractClick={refundContracts.onContractClick}
              onCopy={handleCopy}
              currencyLabel={currencyLabel}
            />
          )}
        </div>
      )}
      <div className={styles.snackbar}>{renderSnackBar()}</div>
      {renderModalList()}
    </div>
  );
};

export default DetailsPageLayout;
